import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { categoryAPI, templateAPI, tenantAPI } from '../services/api'
import { Calendar, ArrowLeft, Save, AlertCircle } from 'lucide-react'
import { OccasionScheduler } from '../components/OccasionScheduler'
import CategoryDropdown from '../components/CategoryDropdown'

const EditTemplatePage = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const occasionSchedulerRef = useRef()
  const [loading, setLoading] = useState(false)
  const [loadingTemplate, setLoadingTemplate] = useState(true)
  const [tenants, setTenants] = useState([])
  const [loadingTenants, setLoadingTenants] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    subcategory_id: '',
    tenants: [], // Changed to array for multiselect
    engineVersion: 'v1',
    tags: [],
    is_public: true,
    isPremium: false,
    // Enhanced occasion fields
    isOccasionDateApplicable: false,
    occasionType: 'none',
    occasionStartDate: '',
    occasionEndDate: '',
    occasionStartTime: '00:01', // Default: 12:01 AM
    occasionEndTime: '23:59',   // Default: 11:59 PM
    isRecurringYearly: false,
    weeklyRecurrenceDays: [],
    monthlyRecurrenceDays: [],
    occasionTimezone: 'Asia/Kolkata',
    occasionMetadata: {}
  })

  const [errors, setErrors] = useState({})

  useEffect(() => {
    fetchTemplate()
    fetchTenants()
  }, [id])

  const fetchTenants = useCallback(async () => {
    try {
      setLoadingTenants(true)
      const response = await tenantAPI.getAll()
      if (response.data.success) {
        setTenants(response.data.data || [])
      }
    } catch (error) {
      console.error('Error fetching tenants:', error)
    } finally {
      setLoadingTenants(false)
    }
  }, [])

  const fetchTemplate = async () => {
    try {
      setLoadingTemplate(true)
      setError(null)

      const response = await templateAPI.getById(id)
      
      if (response.data.success) {
        const template = response.data.data
        setFormData({
          name: template.name || '',
          description: template.description || '',
          category_id: template.category_id || '',
          subcategory_id: template.subcategory_id || template.subcategoryId || '',
          tenants: template.tenants || [],
          engineVersion: template.engineVersion || 'v1',
          tags: template.tags || [],
          is_public: template.is_public !== undefined ? template.is_public : true,
          isPremium: template.isPremium !== undefined ? template.isPremium : false,
          // Enhanced occasion fields
          isOccasionDateApplicable: template.isOccasionDateApplicable || false,
          occasionType: template.occasionType || 'none',
          occasionStartDate: template.occasionStartDate || '',
          occasionEndDate: template.occasionEndDate || '',
          occasionStartTime: template.occasionStartTime || '00:01',
          occasionEndTime: template.occasionEndTime || '23:59',
          isRecurringYearly: template.isRecurringYearly || false,
          weeklyRecurrenceDays: template.weeklyRecurrenceDays || [],
          monthlyRecurrenceDays: template.monthlyRecurrenceDays || [],
          occasionTimezone: template.occasionTimezone || 'Asia/Kolkata',
          occasionMetadata: template.occasionMetadata || {}
        })
      } else {
        setError('Failed to load template')
      }
    } catch (error) {
      console.error('Error fetching template:', error)
      setError('Failed to load template')
    } finally {
      setLoadingTemplate(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const handleTenantChange = (tenantId, isChecked) => {
    setFormData(prev => ({
      ...prev,
      tenants: isChecked
        ? [...prev.tenants, tenantId]
        : prev.tenants.filter(id => id !== tenantId)
    }))
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Template name is required'
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required'
    }

    if (!formData.category_id.trim()) {
      newErrors.category_id = 'Category is required'
    }

    if (!formData.tenants.length) {
      newErrors.tenants = 'At least one tenant must be selected'
    }

    if (!formData.engineVersion.trim()) {
      newErrors.engineVersion = 'Engine version is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      setLoading(true)
      setError(null)

      // Convert tags string to array if needed
      const tagsArray = Array.isArray(formData.tags) 
        ? formData.tags 
        : formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)

      const templateData = {
        name: formData.name,
        description: formData.description,
        category_id: formData.category_id,
        subcategory_id: formData.subcategory_id || undefined, // This will be converted to sub_category_id by field converter
        tenants: formData.tenants,
        engineVersion: formData.engineVersion,
        tags: tagsArray,
        is_public: formData.is_public,
        isPremium: formData.isPremium,
        // Enhanced occasion fields
        isOccasionDateApplicable: formData.isOccasionDateApplicable,
        occasionType: formData.occasionType,
        occasionStartDate: formData.occasionStartDate || null,
        occasionEndDate: formData.occasionEndDate || null,
        occasionStartTime: formData.occasionStartTime || null,
        occasionEndTime: formData.occasionEndTime || null,
        isRecurringYearly: formData.isRecurringYearly,
        weeklyRecurrenceDays: formData.weeklyRecurrenceDays || [],
        monthlyRecurrenceDays: formData.monthlyRecurrenceDays || [],
        occasionTimezone: formData.occasionTimezone || 'Asia/Kolkata',
        occasionMetadata: formData.occasionMetadata || {}
      }

      const response = await templateAPI.update(id, templateData)

      if (response.data.success) {
        setSuccess(true)
        setTimeout(() => {
          navigate('/dashboard')
        }, 2000)
      } else {
        setError(response.data.message || 'Failed to update template')
      }
    } catch (error) {
      console.error('Error updating template:', error)
      setError('Failed to update template. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (loadingTemplate) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading template...</p>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Template Updated Successfully!</h2>
          <p className="text-gray-600 mb-4">Redirecting to dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Edit Template</h1>
              <p className="text-gray-600 mt-1">Update template information and settings</p>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Basic Information */}
            <div className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Template Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter template name"
                />
                {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.description ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Describe your template"
                />
                {errors.description && <p className="text-sm text-red-600 mt-1">{errors.description}</p>}
              </div>
            </div>

            {/* Category and Subcategory */}
            <CategoryDropdown
              value={formData.category_id}
              onChange={(categoryId) => setFormData(prev => ({ ...prev, category_id: categoryId, subcategory_id: '' }))}
              showSubcategories={true}
              subcategoryValue={formData.subcategory_id}
              onSubcategoryChange={(subcategoryId) => setFormData(prev => ({ ...prev, subcategory_id: subcategoryId }))}
              disabled={false}
              error={errors.category_id}
              required={true}
            />

            {/* Tenant Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="tenants" className="block text-sm font-medium text-gray-700 mb-1">
                  Tenants *
                </label>
                <div className={`border rounded-lg p-2 ${
                  errors.tenants ? 'border-red-300' : 'border-gray-300'
                } ${loadingTenants ? 'opacity-50' : ''}`}>
                  {loadingTenants ? (
                    <p className="text-sm text-gray-500 py-2">Loading tenants...</p>
                  ) : tenants.length > 0 ? (
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {tenants.map((tenant) => (
                        <label key={tenant.id} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.tenants.includes(tenant.id)}
                            onChange={(e) => handleTenantChange(tenant.id, e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="text-sm text-gray-700">
                            {tenant.name} ({tenant.domain})
                          </span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 py-2">No tenants available</p>
                  )}
                </div>
                {formData.tenants.length > 0 && (
                  <p className="text-sm text-gray-500 mt-1">
                    {formData.tenants.length} tenant{formData.tenants.length !== 1 ? 's' : ''} selected
                  </p>
                )}
                {errors.tenants && <p className="text-sm text-red-600 mt-1">{errors.tenants}</p>}
              </div>

              <div>
                <label htmlFor="engineVersion" className="block text-sm font-medium text-gray-700 mb-1">
                  Engine Version *
                </label>
                <select
                  id="engineVersion"
                  name="engineVersion"
                  value={formData.engineVersion}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.engineVersion ? 'border-red-300' : 'border-gray-300'
                  }`}
                  required
                >
                  <option value="v1">Version 1.0</option>
                </select>
                {errors.engineVersion && <p className="text-sm text-red-600 mt-1">{errors.engineVersion}</p>}
              </div>
            </div>

            {/* Additional Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-1">
                  Tags
                </label>
                <input
                  type="text"
                  id="tags"
                  name="tags"
                  value={Array.isArray(formData.tags) ? formData.tags.join(', ') : formData.tags}
                  onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter tags separated by commas"
                />
                <p className="text-sm text-gray-500 mt-1">Separate multiple tags with commas</p>
              </div>

              <div className="flex items-center space-x-6 pt-6">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_public"
                    name="is_public"
                    checked={formData.is_public}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_public" className="ml-2 text-sm text-gray-700">
                    Public template
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isPremium"
                    name="isPremium"
                    checked={formData.isPremium}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isPremium" className="ml-2 text-sm text-gray-700">
                    Premium template
                  </label>
                </div>
              </div>
            </div>



            {/* Enhanced Occasion Scheduler */}
            <div className="border-t border-gray-200 pt-6">
              <OccasionScheduler
                value={formData}
                onChange={(occasionConfig) => {
                  setFormData(prev => ({
                    ...prev,
                    ...occasionConfig
                  }))
                }}
                template_id={id}
                className="mb-6"
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end pt-6 border-t border-gray-200">
              <button
                type="submit"
                disabled={loading}
                className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Update Template</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default EditTemplatePage
